/**
 * Print Quiz Common Components & Styles
 * Shared across all 10 print quiz types for consistent Figma-matching layout.
 */

import React, { createContext, useContext, type ReactNode } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import { formatText } from '../../../utils/quiz-parser'
import type { QuizMediaObject } from '../../../types/legacyQuiz'

// ─── Image Scale Multiplier Context ─────────────────────────────
const ImageScaleMultiplierContext = createContext<number>(1)
export const ImageScaleMultiplierProvider = ImageScaleMultiplierContext.Provider

// ─── Additional Image Scales Context ─────────────────────────────
// number = 모든 추가 이미지 인덱스에 동일 배율 적용
// Record<number, number> = 인덱스별 배율
const AdditionalImageScalesContext = createContext<number | Record<number, number>>(1)
export const AdditionalImageScalesProvider = AdditionalImageScalesContext.Provider

export function useAdditionalImageScale(imageIndex: number): number {
  const v = useContext(AdditionalImageScalesContext)
  if (typeof v === 'number') return v
  return v[imageIndex] ?? 1
}

// ─── Option Image Scale Context ─────────────────────────────────
const OptionImageScaleContext = createContext<number>(1)
export const OptionImageScaleProvider = OptionImageScaleContext.Provider

export function useOptionImageScale(): number {
  return useContext(OptionImageScaleContext)
}

// ─── Quiz Number Color Context ──────────────────────────────────
const QuizNumberColorContext = createContext<string | undefined>(undefined)
export const QuizNumberColorProvider = QuizNumberColorContext.Provider

// ─── Hide Quiz Number Context ───────────────────────────────────
// true면 PrintQuizTitle이 번호 배지를 렌더하지 않는다.
// (정답지처럼 카드 외부에서 별도 번호를 표기하는 경우 중복 제거용)
const HideQuizNumberContext = createContext<boolean>(false)
export const HideQuizNumberProvider = HideQuizNumberContext.Provider

// ─── Style Constants ───────────────────────────────────────────

export const PRINT_CORRECT_ANSWER_CLASS = 'font-bold text-[#e50000]'

export const printStyles = {
  container: 'w-full max-w-[650px]',
  quizSection: 'flex flex-col gap-[24px] items-start w-full',
  problemArea: 'pl-[40px] w-full',
  problemLine: 'w-full',
  problemText: 'text-[16px] text-black tracking-[-0.096px] font-normal leading-[48px]',
  titleNumber: 'font-dunggeunmiso text-[#ff9f19] text-[24px] font-bold text-center size-[32px] shrink-0 leading-[32px]',
  titleText: 'text-[16px] text-black font-bold tracking-[-0.096px] leading-[30px] flex-1',
  inputBoxSm: 'bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] h-[40px]',
  inputBoxMd: 'bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] size-[56px]',
  inputBoxLg: 'bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] h-[40px] w-[88px]',
} as const

export const compactPrintStyles = {
  container: 'w-full max-w-[650px]',
  quizSection: 'flex flex-col gap-[18px] items-start w-full',
  problemArea: 'pl-[34px] w-full',
  problemLine: 'w-full',
  problemText: 'text-[15px] text-black tracking-[-0.09px] font-normal leading-[26px]',
  titleNumber: 'font-dunggeunmiso text-[#fa3] text-[24px] font-bold text-center size-[26px] shrink-0 leading-[normal]',
  titleText: 'text-[15px] text-black font-semibold tracking-[-0.09px] leading-[26px] flex-1',
  inputBoxSm: 'bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] h-[35px]',
  inputBoxMd: 'bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] size-[40px]',
  inputBoxLg: 'bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] h-[35px] w-[72px]',
} as const

/** Get the appropriate style set based on compact flag */
export function getStyles(compact?: boolean) {
  return compact ? compactPrintStyles : printStyles
}

// ─── PrintQuizTitle ────────────────────────────────────────────

interface PrintQuizTitleProps {
  number: number
  children: ReactNode
  compact?: boolean
}

/** Renders the quiz number (step-colored) + title text row */
export function PrintQuizTitle({ number, children, compact }: PrintQuizTitleProps) {
  const s = getStyles(compact)
  const numberColor = useContext(QuizNumberColorContext)
  const hideNumber = useContext(HideQuizNumberContext)
  return (
    <div className={cn('flex items-start w-full', hideNumber ? '' : 'gap-[8px]')}>
      {!hideNumber && (
        <span
          className={s.titleNumber}
          style={numberColor ? { color: numberColor } : undefined}
        >
          {number}
        </span>
      )}
      <div className={s.titleText}>{children}</div>
    </div>
  )
}

// ─── PrintQuizProblem ──────────────────────────────────────────

interface PrintQuizProblemProps {
  problem: string | null | undefined
  quizId: string | number
  additionalImages?: QuizMediaObject[] | null
  compact?: boolean
  /** counting/coloring: `{{}}` 빈칸 마커 제거 후 렌더 */
  stripBlanks?: boolean
}

/**
 * Title 아래에 노출되는 문제 본문. `{image=N}` 태그를 `additionalImages`로 치환한다.
 * 학생 모드의 `quiz.problem` 렌더링과 동일한 의미를 print에 제공한다.
 */
export function PrintQuizProblem({
  problem,
  quizId,
  additionalImages,
  compact,
  stripBlanks,
}: PrintQuizProblemProps) {
  if (!problem) return null
  const text = stripBlanks ? problem.replace(/\{\{\}\}/g, '').trim() : problem
  if (!text) return null
  const s = getStyles(compact)
  return (
    <div className={s.problemArea}>
      <div
        className={cn(
          'text-black font-normal',
          compact
            ? 'text-[15px] tracking-[-0.09px] leading-[26px]'
            : 'text-[16px] tracking-[-0.096px] leading-[28px]',
        )}
      >
        {formatText(text, `${quizId}-problem`, additionalImages ?? undefined)}
      </div>
    </div>
  )
}

// ─── PrintQuizImage ────────────────────────────────────────────

interface PrintQuizImageProps {
  src: string
  alt?: string
  className?: string
  /** printScale 값 — 있으면 해당 비율 적용, 없으면 기본 75% */
  printScale?: number | null
}

/** 기본 이미지 스케일 (원본 대비 75%) */
const DEFAULT_PRINT_SCALE = 0.75

/**
 * Renders a centered quiz image.
 * 이미지 원본 width 기준으로 scale 적용 (onLoad로 naturalWidth 계산).
 * - printScale 있으면: 원본 × printScale
 * - 없으면: 원본 × 0.75 (기본)
 */
export function PrintQuizImage({ src, alt = 'quiz', className, printScale }: PrintQuizImageProps) {
  const scaleMultiplier = useContext(ImageScaleMultiplierContext)
  const baseScale = printScale ?? DEFAULT_PRINT_SCALE
  const scale = baseScale * scaleMultiplier
  const imgRef = React.useRef<HTMLImageElement | null>(null)
  const [naturalWidth, setNaturalWidth] = React.useState<number | null>(null)

  const handleLoad = React.useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    setNaturalWidth(e.currentTarget.naturalWidth)
  }, [])

  // 캐시된 이미지는 onLoad가 보장되지 않으므로 마운트 직후 동기적으로 naturalWidth를 읽는다.
  // 페인트 전에 width 스타일이 반영돼야 hidden measurement DOM이 정확한 height를 얻을 수 있다.
  React.useLayoutEffect(() => {
    const img = imgRef.current
    if (img && img.complete && img.naturalWidth > 0) {
      setNaturalWidth((prev) => (prev === img.naturalWidth ? prev : img.naturalWidth))
    }
  }, [src])

  const scaledWidth = naturalWidth != null ? Math.round(naturalWidth * scale) : null

  return (
    <div className="flex justify-center w-full">
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={cn('h-auto', className)}
        style={scaledWidth != null ? { width: `${scaledWidth}px` } : undefined}
        onLoad={handleLoad}
      />
    </div>
  )
}

// ─── PrintOptionImage ─────────────────────────────────────────────

interface PrintOptionImageProps {
  src: string
  alt?: string
  className?: string
  /** 기준 max-height(px). 기본 80 (현재 multi-choice 동작과 동일) */
  baseMaxHeightPx?: number
}

/**
 * 보기 이미지 (multi-choice answer.image, pairing pair, ordering bank item) 공용 렌더러.
 * `OptionImageScaleContext`의 multiplier를 max-height에 곱한다.
 * 1.0 = baseMaxHeightPx(기본 80px) 그대로.
 */
export function PrintOptionImage({ src, alt = 'option', className, baseMaxHeightPx = 80 }: PrintOptionImageProps) {
  const scale = useOptionImageScale()
  const finalMaxH = Math.round(baseMaxHeightPx * scale)
  return (
    <img
      src={src}
      alt={alt}
      className={cn('w-auto object-contain shrink-0', className)}
      style={{ maxHeight: `${finalMaxH}px` }}
    />
  )
}

// ─── PrintInputBox ─────────────────────────────────────────────

interface PrintInputBoxProps {
  size?: 'sm' | 'md' | 'lg'
  children?: ReactNode
  className?: string
  compact?: boolean
}

/** Input box matching Figma spec: sm=40h, md=56x56, lg=88w×40h (compact: sm=35h, md=40x40, lg=72w×35h) */
export function PrintInputBox({ size = 'md', children, className, compact }: PrintInputBoxProps) {
  const s = getStyles(compact)
  const sizeClass = {
    sm: s.inputBoxSm,
    md: s.inputBoxMd,
    lg: s.inputBoxLg,
  }[size]

  return (
    <div className={cn(sizeClass, 'flex justify-center items-center text-center', className)}>
      {children}
    </div>
  )
}

// ─── PrintWordBank ─────────────────────────────────────────────

interface PrintWordBankProps {
  items: { key: string; label: string }[]
  label?: string
  compact?: boolean
}

/** "보기" word bank box (used by drag_text and ordering) */
export function PrintWordBank({ items, label = '보기', compact }: PrintWordBankProps) {
  if (items.length === 0) return null
  const s = getStyles(compact)

  return (
    <div className={cn('relative bg-[#f2f2f2] rounded-[16px] w-full', compact ? 'pt-[38px] pb-[16px] px-[20px]' : 'pt-[48px] pb-[24px] px-[24px]')}>
      {/* "보기" badge */}
      <span className={cn('absolute top-0 left-[20px] bg-[#999] text-white rounded-bl-[8px] rounded-br-[8px] flex items-center font-bold', compact ? 'h-[28px] text-[14px] w-[54px] justify-center' : 'h-[28px] px-[12px] text-[14px]')}>
        {label}
      </span>
      {/* Items */}
      <div className="flex flex-wrap gap-[10px]">
        {items.map((item, idx) => (
          <span
            key={item.key || `wb-${idx}`}
            className={cn(
              s.inputBoxSm,
              'inline-flex items-center justify-center px-[16px] py-[8px] text-black tracking-[-0.09px] text-center',
              compact ? 'min-w-[72px] text-[15px]' : 'min-w-[80px] text-[16px]'
            )}
          >
            <KatexText text={item.label} />
          </span>
        ))}
      </div>
    </div>
  )
}

// ─── PrintOrderBoxes ───────────────────────────────────────────

interface PrintOrderBoxesProps {
  count: number
  correctOrder?: Map<number, number>
  showCorrectAnswers?: boolean
  compact?: boolean
}

/** Order boxes with → arrows between them (for ordering quiz) */
export function PrintOrderBoxes({ count, correctOrder, showCorrectAnswers = false, compact }: PrintOrderBoxesProps) {
  return (
    <div className={cn('flex items-center flex-wrap', compact ? 'gap-[4px]' : 'gap-[8px]')}>
      {Array.from({ length: count }).map((_, idx) => {
        const orderNum = correctOrder?.get(idx)
        const showNum = showCorrectAnswers && orderNum != null
        return (
          <div key={idx} className={cn('flex items-center', compact ? 'gap-[4px]' : 'gap-[8px]')}>
            <PrintInputBox
              size="md"
              compact={compact}
              className={cn(
                'text-[16px]',
                showNum ? PRINT_CORRECT_ANSWER_CLASS : 'font-semibold text-gray-500',
              )}
            >
              {showNum ? orderNum : '\u00A0'}
            </PrintInputBox>
            {idx < count - 1 && (
              <span className="text-[20px] text-[#ccc]">→</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Helper: get print scale ──────────────────────────────────

/**
 * Extract printScale from quiz image objects.
 * Bundle imagePrint 또는 Content image의 printScale을 추출합니다.
 * 기본 print 이미지 크기는 75%. printScale 있으면 해당 비율로 대체.
 *
 * @param image - 원본 이미지 (Content 기반 printScale 포함 가능)
 * @param printImage - Bundle 기반 인쇄용 이미지 (printScale 포함 가능)
 * @returns printScale 값 또는 null (기본 75% 사용)
 */
export function getPrintScale(
  image?: { printScale?: number } | null,
  printImage?: { printScale?: number } | null,
): number | null {
  // Bundle imagePrint의 printScale 우선
  if (printImage?.printScale != null) return printImage.printScale
  // Content 기반 printScale
  if (image?.printScale != null) return image.printScale
  return null
}

// ─── Helper: get image URL ─────────────────────────────────────

/**
 * Safely extract image URL from quiz image object.
 * Print 컴포넌트에서는 고해상도 인쇄용 이미지를 우선 사용합니다.
 *
 * 우선순위:
 * 1. printImage (Bundle 기반 imagePrint) — signedUrl > url
 * 2. image.printSignedUrl (Content 기반 인쇄용 URL)
 * 3. image.signedUrl > image.url (일반 이미지 fallback)
 */
export function getImageUrl(
  image?: { signedUrl?: string; url?: string; printSignedUrl?: string } | null,
  printImage?: { signedUrl?: string; url?: string } | null,
): string | null {
  // 1. Bundle 기반 인쇄용 이미지 (imagePrint)
  if (printImage) {
    const printUrl = printImage.signedUrl || printImage.url
    if (printUrl) return printUrl
  }
  if (!image) return null
  // 2. Content 기반 인쇄용 URL (printSignedUrl)
  if (image.printSignedUrl) return image.printSignedUrl
  // 3. 일반 이미지 fallback
  return image.signedUrl || image.url || null
}
